import {computed, DestroyRef, effect, inject, Injectable, OnDestroy, signal, untracked} from "@angular/core"
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {ThreeMesh} from "@app/template-editor/helpers/three-mesh"
import {asFloat, ThreeColormassMaterial, ThreeShadowCatcher} from "@cm/material-nodes"
import {Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {Matrix4, Vector3} from "@cm/math"
import {ObjectId, SceneNodes, ObjectPathPart, SceneNodePart, getRootTemplateNodeForObjectPath, objectPathStartsWith} from "@cm/template-nodes"
import {objectDifferent} from "@template-editor/helpers/change-detection"
import {ThreeAreaLight} from "@template-editor/helpers/three-area-light"
import {ThreeCamera} from "@template-editor/helpers/three-camera"
import {ThreeEnvironment} from "@template-editor/helpers/three-environment"
import {ThreeGrid} from "@template-editor/helpers/three-grid"
import {ThreeObject, ThreeObjectPart} from "@template-editor/helpers/three-object"
import {ThreeProgressiveUVShadowMap} from "@template-editor/helpers/uv-shadow-map/three-progressive-uv-shadow-map"
import {SceneManagerService} from "@template-editor/services/scene-manager.service"
import {ThreeMaterialManagerService} from "@template-editor/services/three-material-manager.service"
import {concat, concatMap, defer, delay, delayWhen, filter, from, of, range, Subject, switchMap, take, tap, timer} from "rxjs"
import {ThreeAnnotation} from "@app/template-editor/helpers/three-annotation"
import {ThreeLightPortal} from "@app/template-editor/helpers/three-light-portal"
import {ThreeMeshCurveControl} from "@app/template-editor/helpers/three-mesh-curve-control"
import {ThreePoint} from "@app/template-editor/helpers/three-point"
import {ThreePreloadMaterial} from "@app/template-editor/helpers/three-preload-material"
import {ThreeRectangle} from "@app/template-editor/helpers/three-rectangle"
import {ThreeSeam} from "@app/template-editor/helpers/three-seam"
import {ThreeDimensionGuides} from "@app/template-editor/helpers/three-dimension-guides"
import {ThreeWireframeMesh} from "@app/template-editor/helpers/three-wireframe-mesh"
import {ThreeCuboid} from "@app/template-editor/helpers/three-cuboid"
import {computeApertureSizeCm} from "@app/template-editor/helpers/aperture"
import {CM_LOGGER_SCENE} from "@app/common/services/logging"
import type {CmLogger} from "@cm/log"
import {ThreePointLight} from "@app/template-editor/helpers/three-point-light"
import {ThreeSunLight} from "@app/template-editor/helpers/three-sun-light"

export const SHADOW_MAP_COMPUTATION_DELAY = 1000
export const DEFAULT_NUM_SHADOW_MAP_OUTER_UPDATE_ITERATIONS = 75
export const DEFAULT_NUM_SHADOW_MAP_INNER_UPDATE_ITERATIONS = 4
export const DEFAULT_NUM_SHADOW_MAP_OUTER_SMOOTH_ITERATIONS = 0
export const DEFAULT_NUM_SHADOW_MAP_INNER_SMOOTH_ITERATIONS = 1
export const DEFAULT_SHADOW_MAP_RESOLUTION = 1024

export type DisplayMode = "configurator" | "editor"

const defaultGrid: SceneNodes.Grid = {
    type: "Grid",
    id: "grid",
    size: 1000,
    divisions: 10,
    color1: [0.26, 0.26, 0.26],
    color2: [0.73, 0.73, 0.73],
    transform: Matrix4.identity(),
    path: [],
}

const defaultEnvironment: SceneNodes.Environment = {
    id: "defaultEnvironment",
    type: "Environment",
    envData: {
        type: "url",
        url: "assets/images/template-editor-envmap.exr",
        originalFileExtension: "exr",
    },
    intensity: 1,
    rotation: new Vector3(0, 0, 0),
    mirror: false,
    saturation: 100,
    path: [],
}

@Injectable()
export class ThreeSceneManagerService implements OnDestroy {
    readonly $forceWebGl = signal(false)
    readonly $ambientLight = signal(true)
    readonly $showGrid = signal(true)
    readonly $environmentNode = signal<THREE.Node<"vec3"> | undefined>(undefined)
    readonly $displayMode = signal<DisplayMode>("editor")
    readonly $showProgressiveLightMapDebug = signal(false)
    readonly $showUnselectedCurves = signal(false)
    showUnselectedCurves$ = toObservable(this.$showUnselectedCurves)
    readonly $orbitIsDragging = signal(false)
    readonly $renderer = signal<THREE.WebGPURenderer | undefined>(undefined)

    readonly sceneManagerService = inject(SceneManagerService)
    readonly materialManagerService = inject(ThreeMaterialManagerService)
    private readonly destroyRef = inject(DestroyRef)
    private readonly logger: CmLogger = inject(CM_LOGGER_SCENE)

    protected threeFactory: {
        [key in SceneNodes.SceneNode["type"]]?: {
            new (threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void): ThreeObject
        }
    } = {
        Grid: ThreeGrid,
        Mesh: ThreeMesh,
        MeshCurveControl: ThreeMeshCurveControl,
        WireframeMesh: ThreeWireframeMesh,
        Environment: ThreeEnvironment,
        AreaLight: ThreeAreaLight,
        PointLight: ThreePointLight,
        SunLight: ThreeSunLight,
        Camera: ThreeCamera,
        Rectangle: ThreeRectangle,
        Point: ThreePoint,
        Annotation: ThreeAnnotation,
        LightPortal: ThreeLightPortal,
        PreloadMaterial: ThreePreloadMaterial,
        Seam: ThreeSeam,
        DimensionGuides: ThreeDimensionGuides,
        Cuboid: ThreeCuboid,
    }

    private objectCache = new Map<ObjectId, ThreeObject>()
    private baseScene = new THREE.Scene()

    getObjectsFromSceneNodes(sceneNode: SceneNodePart[]): ThreeObjectPart[] {
        const allThreeObjects = Array.from(this.objectCache.values())

        return sceneNode
            .map((sceneNodePart) => {
                return allThreeObjects
                    .filter((threeObject) => {
                        const objectSceneNode = threeObject.getSceneNode()
                        return sceneNodePart.sceneNode.id === objectSceneNode.id
                    })
                    .map((threeObject) => ({
                        threeObject,
                        part: sceneNodePart.part,
                    }))
            })
            .flat()
    }

    private getSceneNodesForObjectPathPart(objectPathParts: ObjectPathPart[]): SceneNodePart[] {
        const scene = this.sceneManagerService.$scene()

        const selectionWithDuplicates = objectPathParts.flatMap((part) =>
            scene
                .filter((sceneNode) => objectPathStartsWith(sceneNode.path, part.path))
                .map<SceneNodePart>((matchingSceneNode) => ({sceneNode: matchingSceneNode, part: part.part})),
        )
        const selection = selectionWithDuplicates.filter((part, index) => {
            return index === selectionWithDuplicates.findIndex((p) => p.sceneNode.id === part.sceneNode.id && p.part === part.part)
        })

        return selection
    }

    private readonly $selectedSceneNodes = computed(() => {
        const selectedObjectPathParts = this.sceneManagerService.$selectedObjectPathParts()
        return this.getSceneNodesForObjectPathPart(selectedObjectPathParts)
    })

    private readonly $selectedTopLevelSceneNodes = computed(() => {
        const selectedObjectPathParts = this.sceneManagerService.$selectedObjectPathParts()
        const topLevelSelectedObjectPathParts = selectedObjectPathParts.map<ObjectPathPart>((part) => ({
            path: [getRootTemplateNodeForObjectPath(part.path)],
            part: "root",
        }))
        return this.getSceneNodesForObjectPathPart(topLevelSelectedObjectPathParts)
    })

    private readonly $hoveredSceneNodes = computed(() => {
        const hoveredObjectPathParts = this.sceneManagerService.$hoveredObjectPathParts()
        return this.getSceneNodesForObjectPathPart(hoveredObjectPathParts)
    })

    private readonly $hoveredTopLevelSceneNodes = computed(() => {
        const hoveredObjectPathParts = this.sceneManagerService.$hoveredObjectPathParts()
        const topLevelHoveredObjectPathParts = hoveredObjectPathParts.map<ObjectPathPart | false>((part) => {
            if (part.path.length === 0) return false
            return {
                path: [getRootTemplateNodeForObjectPath(part.path)],
                part: "root",
            }
        })
        return this.getSceneNodesForObjectPathPart(topLevelHoveredObjectPathParts.filter((part): part is ObjectPathPart => part !== false))
    })

    readonly $outlinedObjects = computed(() => this.getObjectsFromSceneNodes(this.$selectedSceneNodes()))
    readonly $outlinedTopLevelObjects = computed(() => this.getObjectsFromSceneNodes(this.$selectedTopLevelSceneNodes()))
    readonly $hoveredObjects = computed(() => this.getObjectsFromSceneNodes(this.$hoveredSceneNodes()))
    readonly $hoveredTopLevelObjects = computed(() => this.getObjectsFromSceneNodes(this.$hoveredTopLevelSceneNodes()))

    private readonly nonTaaFrameFinished = new Subject<void>()
    private readonly integrateTaaSubject = new Subject<void>()
    private taaGeneration = 0

    private requestedRedraw = new Subject<{taaFrame: number | null; crossfadeAlpha: number}>()
    requestedRedraw$ = this.requestedRedraw.asObservable()

    private sceneGeometryChanged = new Subject<void>()
    sceneGeometryChanged$ = this.sceneGeometryChanged.asObservable()

    private requestedResize = new Subject<void>()
    requestedResize$ = this.requestedResize.asObservable()

    readonly $cursorPosition = signal<Vector3 | undefined>(undefined)
    private readonly $cursorObject = computed(() => {
        const cursorPosition = this.$cursorPosition()
        if (!cursorPosition) return undefined

        const transform = new Matrix4()
        transform.setTranslation(cursorPosition)
        const returnValue: SceneNodes.Point = {id: "cursorObject", type: "Point", size: 20, transform, path: []}
        return returnValue
    })

    readonly $useBvh = computed<boolean>(() => {
        if (this.$displayMode() === "editor") return true

        for (const {sceneNode} of this.$managedScene()) {
            if (SceneNodes.Camera.is(sceneNode) && sceneNode.autoFocus && computeApertureSizeCm(sceneNode.focalLength, sceneNode.fStop) >= 0.1) return true
            if (SceneNodes.Annotation.is(sceneNode) || (SceneNodes.DimensionGuides.is(sceneNode) && sceneNode.concealable)) return true
        }

        return false
    })

    private shadowMapMeshes = new Set<THREE.Mesh<THREE.BufferGeometry, THREE.Material>>()
    private shadowMapMeshesChanged = new Subject<void>()

    private readonly $sceneOptions = computed(() => this.sceneManagerService.$scene().find(SceneNodes.SceneOptions.is))
    private readonly $textureFiltering = computed(() => this.$sceneOptions()?.textureFiltering)
    private readonly $textureStreamingMode = computed(() => this.$sceneOptions()?.textureStreamingMode)
    private readonly $shadowCatcherFalloff = computed(() => this.$sceneOptions()?.shadowCatcherFalloff, {
        equal: (a, b) => (a === undefined ? a === b : !objectDifferent(a, b, undefined)),
    })
    private readonly $environmentMapMode = computed(() => this.$sceneOptions()?.environmentMapMode)
    private readonly $materialModifier = computed(() => {
        const shadowCatcherFalloff = this.$shadowCatcherFalloff()
        const environmentNode = this.$environmentNode()
        const environmentMapMode = this.$environmentMapMode()
        const reviewMode = this.sceneManagerService.$reviewMode()

        return (material: THREE.Material) => {
            if (material instanceof ThreeShadowCatcher) {
                if (shadowCatcherFalloff) material.setFilterParameters({...shadowCatcherFalloff, bias: 0.0})
                else material.resetFilterParameters()
            }

            const specularOnlyMaterialModifier = (material: THREE.Material, remove: boolean) => {
                if (!(material instanceof THREE.MeshStandardNodeMaterial)) return false
                let envNode: THREE.Node<"vec3"> | null = null

                if (material.roughnessNode) {
                    if (remove) {
                        envNode = null
                    } else {
                        envNode = environmentNode?.mul(TSL.pow(TSL.sub(1.0, TSL.clamp(asFloat(material.roughnessNode), 0.0, 1.0)), 4.0)) ?? null
                    }
                }

                const changed = material.envNode !== envNode
                material.envNode = envNode
                return changed
            }

            if (reviewMode === "wireframe") {
                material.polygonOffset = true
                material.polygonOffsetFactor = 5
            } else {
                material.polygonOffset = false
                material.polygonOffsetFactor = 0
            }

            if (specularOnlyMaterialModifier(material, environmentMapMode !== "specularOnly")) material.needsUpdate = true
        }
    })
    private readonly $renderLights = computed(() => this.$sceneOptions()?.enableRealtimeLights !== false)
    private readonly $realtimeShadowMapOptions = computed(() => this.$sceneOptions()?.realtimeShadowMapOptions)
    private readonly $shadowMapResolution = computed(() => this.$realtimeShadowMapOptions()?.resolution ?? DEFAULT_SHADOW_MAP_RESOLUTION)
    private readonly $shadowMapOuterUpdateIterations = computed(
        () => this.$realtimeShadowMapOptions()?.outerUpdateIterations ?? DEFAULT_NUM_SHADOW_MAP_OUTER_UPDATE_ITERATIONS,
    )
    private readonly $shadowMapInnerUpdateIterations = computed(
        () => this.$realtimeShadowMapOptions()?.innerUpdateIterations ?? DEFAULT_NUM_SHADOW_MAP_INNER_UPDATE_ITERATIONS,
    )
    private readonly $shadowMapSmoothOuterIterations = computed(
        () => this.$realtimeShadowMapOptions()?.outerSmoothIterations ?? DEFAULT_NUM_SHADOW_MAP_OUTER_SMOOTH_ITERATIONS,
    )
    private readonly $shadowMapSmoothInnerIterations = computed(
        () => this.$realtimeShadowMapOptions()?.innerSmoothIterations ?? DEFAULT_NUM_SHADOW_MAP_INNER_SMOOTH_ITERATIONS,
    )

    private readonly $staticDisplayList = computed<SceneNodes.SceneNode[]>(() => [
        ...(this.$showGrid() ? [defaultGrid] : []),
        ...(() => {
            const cursorObject = this.$cursorObject()
            return cursorObject ? [cursorObject] : []
        })(),
    ])
    private readonly $renderScene = computed(() => {
        const getScene = () => {
            const scene = this.sceneManagerService.$computeDummyReifiedMeshData()
                ? this.sceneManagerService.$scene().filter((x) => !SceneNodes.PreloadMaterial.is(x))
                : this.sceneManagerService.$scene()
            const reviewMode = this.sceneManagerService.$reviewMode()
            const channel = reviewMode === "wireframe" ? this.sceneManagerService.$reviewFocus() : this.sceneManagerService.$reviewedUvChannel()

            const sceneAdaptedForLights =
                !this.$renderLights() || reviewMode !== undefined
                    ? scene.map((x) => {
                          if (SceneNodes.AreaLight.is(x) || SceneNodes.PointLight.is(x) || SceneNodes.SunLight.is(x)) {
                              return {...x, on: false}
                          }
                          return x
                      })
                    : scene
            const sceneAdaptedForWireframes =
                reviewMode !== undefined
                    ? [
                          ...sceneAdaptedForLights,
                          ...sceneAdaptedForLights.filter(SceneNodes.Mesh.is).map(({completeMeshData, transform, id, path}) => {
                              const ret: SceneNodes.WireframeMesh = {
                                  type: "WireframeMesh",
                                  completeMeshData,
                                  transform,
                                  id: id + "_wireframe",
                                  path,
                                  channel,
                              }
                              return ret
                          }),
                      ]
                    : sceneAdaptedForLights

            return sceneAdaptedForWireframes
        }

        const scene = [...getScene(), ...this.$staticDisplayList()]

        const hasIllumination = () =>
            scene.some(
                (x) => ((SceneNodes.AreaLight.is(x) || SceneNodes.PointLight.is(x) || SceneNodes.SunLight.is(x)) && x.on) || SceneNodes.Environment.is(x),
            )

        if (!hasIllumination() && this.$ambientLight()) {
            scene.push(defaultEnvironment)
        }

        return scene
    })
    private readonly $managedScene = computed(() => {
        const scene = new Map<string, {sceneNode: SceneNodes.SceneNode; visible: boolean}>()
        this.$renderScene().forEach((sceneNode) => {
            if (scene.has(sceneNode.id)) return
            scene.set(sceneNode.id, {sceneNode, visible: true})
        })

        const visibleSceneItems = [...scene.values()]
        visibleSceneItems.forEach(({sceneNode}) => {
            if (SceneNodes.MeshCurveControl.is(sceneNode)) {
                const {mesh} = sceneNode
                if (scene.has(mesh.id)) return
                scene.set(mesh.id, {sceneNode: mesh, visible: false})
            } else if (SceneNodes.Seam.is(sceneNode)) {
                sceneNode.item.forEach((item) => {
                    if (scene.has(item.id)) return
                    scene.set(item.id, {sceneNode: item, visible: false})
                })
            }
        })

        const retVal = [...scene.values()]
        retVal.sort(({sceneNode: a}, {sceneNode: b}) => {
            if (SceneNodes.Mesh.is(a) && !SceneNodes.Mesh.is(b)) return -1
            else if (!SceneNodes.Mesh.is(a) && SceneNodes.Mesh.is(b)) return 1
            else return 0
        })

        return retVal
    })
    private readonly $hasLights = computed(() =>
        this.$renderScene().some((x) => (SceneNodes.AreaLight.is(x) || SceneNodes.PointLight.is(x) || SceneNodes.SunLight.is(x)) && x.on),
    )

    readonly $hasIllumination = computed(() => this.$hasLights() || this.sceneManagerService.$scene().some((x) => SceneNodes.Environment.is(x)))
    private readonly $hasMeshes = computed(() => this.$renderScene().some(SceneNodes.Mesh.is))

    private readonly $initialTemplateLoadCompleted = signal(false)

    private readonly $requiresProgressiveLightMap = computed(
        () => this.$hasLights() && this.$hasMeshes() && this.$sceneOptions()?.enableRealtimeShadows !== false && this.$initialTemplateLoadCompleted(),
    )
    private progressiveLightMap: ThreeProgressiveUVShadowMap | undefined
    private progressiveLightMapUpdate = new Subject<void>()

    private threeObjectUpdated = new Subject<{threeObject: ThreeObject; asyncUpdate: boolean}>()
    threeObjectUpdated$ = this.threeObjectUpdated.asObservable()

    private initPromise: Promise<void> | undefined
    private updatePendingFrameId: number | null = null

    private preRender = new Subject<{camera: THREE.Camera; taaFrame: number | null}>()
    preRender$ = this.preRender.asObservable()

    constructor() {
        this.sceneManagerService.templateSwapped$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => {
                    this.$initialTemplateLoadCompleted.set(false)
                    this.sceneManagerService.compileTemplate()
                }),
                switchMap(() =>
                    this.sceneManagerService.criticalTasks$.pipe(
                        filter((criticalTask) => criticalTask.length === 0),
                        take(1),
                        delay(SHADOW_MAP_COMPUTATION_DELAY),
                    ),
                ),
            )
            .subscribe(() => {
                this.$initialTemplateLoadCompleted.set(true)
            })

        this.materialManagerService.requestedRedraw$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.redraw({strategy: "integrate"}))

        effect(() => {
            const environmentNode = this.$environmentNode()
            this.modifyBaseScene((scene) => {
                scene.environmentNode = environmentNode
            })
        })

        effect(() => {
            const selectedSceneNodes = this.$selectedSceneNodes()
                .filter((sceneNodePart) => sceneNodePart.sceneNode.selectable !== false)
                .map((sceneNodePart) => sceneNodePart.sceneNode)

            this.objectCache.forEach((threeObject) => {
                const objectSceneNode = threeObject.getSceneNode()
                threeObject.setSelected(selectedSceneNodes.some((selectedSceneNode) => selectedSceneNode.id === objectSceneNode.id))
            })
            this.redraw()
        })

        effect(() => {
            const displayMode = this.$displayMode()
            this.objectCache.forEach((threeObject) => {
                threeObject.onDisplayModeChange(displayMode)
            })
            this.redraw()
        })

        effect(() => {
            this.updateScene()
        })

        effect(() => {
            const showProgressiveLightMapDebug = untracked(this.$showProgressiveLightMapDebug)

            if (this.$requiresProgressiveLightMap()) {
                if (!this.progressiveLightMap) {
                    const renderer = this.$renderer()
                    if (!renderer) return

                    this.progressiveLightMap = new ThreeProgressiveUVShadowMap(renderer, this.$shadowMapResolution())

                    if (showProgressiveLightMapDebug) this.baseScene.add(this.progressiveLightMap.getDebugObject())
                    this.progressiveLightMap.attachedUVShadowMap$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mesh) => {
                        if (!this.progressiveLightMap) throw new Error("Progressive light map not set")
                        const {material} = mesh
                        if (!(material instanceof ThreeColormassMaterial)) return

                        const newMaterial = this.materialManagerService.acquireVariation(material, (material): material is ThreeColormassMaterial => {
                            return material instanceof ThreeColormassMaterial && material.uvShadowMapNode !== null
                        })

                        const newShadowMap = this.progressiveLightMap.getUVShadowMap()
                        if (newMaterial.uvShadowMapNode?.value !== newShadowMap) {
                            newMaterial.uvShadowMapNode = TSL.texture(newShadowMap)
                            newMaterial.needsUpdate = true
                        }

                        this.materialManagerService.releaseMaterial(material)
                        mesh.material = newMaterial
                    })
                    this.progressiveLightMap.detachedUVShadowMap$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mesh) => {
                        this.detachShadowMapMesh(mesh)
                    })

                    untracked(() => this.shadowMapMeshesChanged.next())
                } else {
                    const resolution = this.$shadowMapResolution()
                    if (this.progressiveLightMap.getResolution() !== resolution) {
                        this.progressiveLightMap.setResolution(resolution)
                        untracked(() => this.shadowMapMeshesChanged.next())
                    }
                }
            } else {
                if (this.progressiveLightMap) {
                    if (showProgressiveLightMapDebug) this.baseScene.remove(this.progressiveLightMap.getDebugObject())
                    this.progressiveLightMap.dispose()
                    this.progressiveLightMap = undefined
                    this.redraw()
                }
            }
        })

        effect(() => {
            this.$shadowMapOuterUpdateIterations()
            this.$shadowMapInnerUpdateIterations()
            this.$shadowMapSmoothOuterIterations()
            this.$shadowMapSmoothInnerIterations()
            this.requestShadowMapUpdate()
        })

        effect(() => {
            const showProgressiveLightMapDebug = this.$showProgressiveLightMapDebug()
            if (!this.progressiveLightMap) return

            if (showProgressiveLightMapDebug) this.baseScene.add(this.progressiveLightMap.getDebugObject())
            else this.baseScene.remove(this.progressiveLightMap.getDebugObject())
            this.redraw()
        })

        effect(() => {
            this.materialManagerService.materialModifier = this.$materialModifier()
            this.materialManagerService.updateMaterials(this.materialManagerService.materialModifier)
            this.redraw()
        })

        effect(() => {
            if (this.$useBvh()) {
                this.$managedScene()
                    .map((x) => this.objectCache.get(x.sceneNode.id))
                    .filter((x) => x instanceof ThreeMesh)
                    .forEach((threeMesh) => {
                        if (!threeMesh.hasBvh()) threeMesh.computeBvh()
                    })
            }
        })

        effect(() => {
            this.materialManagerService.textureTilesManager.setTextureFiltering(this.$textureFiltering() ?? false)
        })

        effect(() => {
            const streamingMode = this.$textureStreamingMode() ?? "high-quality"
            if (streamingMode !== "off") {
                this.materialManagerService.textureTilesManager.setStreamingMode(streamingMode)
            }
        })

        this.shadowMapMeshesChanged
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(() => this.progressiveLightMap !== undefined),
                switchMap(() => timer(this.$displayMode() === "configurator" ? 10 : 1000).pipe(switchMap(() => from(this.sync(false))))),
            )
            .subscribe(() => this.shadowMapMeshesSettled())

        const shadowUpdate = async (outerIteration: number) => {
            return new Promise<void>((resolve) => {
                if (!this.progressiveLightMap) resolve()

                requestAnimationFrame(() => {
                    if (this.progressiveLightMap) {
                        //if (iteration === 0) this.progressiveLightMap.reset()
                        const shadowMapComputeInnerIterations = this.$shadowMapInnerUpdateIterations()
                        const iteration = outerIteration * shadowMapComputeInnerIterations
                        this.progressiveLightMap.compute(this.baseScene, iteration, shadowMapComputeInnerIterations)
                        this.redraw({immediate: true})
                    }
                    resolve()
                })
            })
        }

        const shadowSmooth = async (outerIteration: number) => {
            return new Promise<void>((resolve) => {
                if (!this.progressiveLightMap) resolve()

                requestAnimationFrame(() => {
                    if (this.progressiveLightMap) {
                        const shadowMapSmoothInnerIterations = this.$shadowMapSmoothInnerIterations()
                        const iteration = outerIteration * shadowMapSmoothInnerIterations
                        this.progressiveLightMap.smoothResult(iteration, shadowMapSmoothInnerIterations)
                        this.redraw({immediate: true})
                    }
                    resolve()
                })
            })
        }

        const pauseWhenOrbitIsDragging = toObservable(this.$orbitIsDragging).pipe(filter((isDragging) => !isDragging))

        //If for 1000ms there are no other progressive light map update events, start accumulating shadow map updates
        this.progressiveLightMapUpdate
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() =>
                    timer(this.$displayMode() === "configurator" ? 10 : 1000).pipe(
                        switchMap(() =>
                            concat(
                                range(this.$shadowMapOuterUpdateIterations()).pipe(
                                    concatMap((iteration) =>
                                        defer(() =>
                                            of(iteration).pipe(
                                                delayWhen(() => (this.$orbitIsDragging() ? pauseWhenOrbitIsDragging : of(undefined))),
                                                switchMap(() => from(shadowUpdate(iteration))),
                                            ),
                                        ),
                                    ),
                                ),
                                range(this.$shadowMapSmoothOuterIterations()).pipe(
                                    concatMap((iteration) =>
                                        defer(() =>
                                            of(iteration).pipe(
                                                delayWhen(() => (this.$orbitIsDragging() ? pauseWhenOrbitIsDragging : of(undefined))),
                                                switchMap(() => from(shadowSmooth(iteration))),
                                            ),
                                        ),
                                    ),
                                ),
                            ),
                        ),
                    ),
                ),
            )
            .subscribe()

        const TAA_FRAMES = 64

        const taaUpdate = async (iteration: number, generation: number, crossfadeAlpha = 1.0) => {
            return new Promise<void>((resolve) => {
                requestAnimationFrame(() => {
                    if (this.taaGeneration === generation) {
                        this.redrawImpl(iteration, crossfadeAlpha)
                    }
                    resolve()
                })
            })
        }

        //If for 100ms there are no other rendering finished events, start accumulating 32 TAA, stop if another rendering finished event is emitted
        this.nonTaaFrameFinished
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => {
                    const generation = ++this.taaGeneration
                    return timer(100).pipe(
                        switchMap(() => range(TAA_FRAMES).pipe(concatMap((iteration) => defer(() => from(taaUpdate(iteration, generation)))))),
                    )
                }),
            )
            .subscribe()

        // "integrate" redraw: crossfade from current accumulated TAA result into a fresh TAA sequence
        this.integrateTaaSubject
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => {
                    const generation = this.taaGeneration // already incremented by redraw(); do NOT increment again
                    return range(TAA_FRAMES).pipe(concatMap((iteration) => defer(() => from(taaUpdate(iteration, generation, iteration / (TAA_FRAMES - 1))))))
                }),
            )
            .subscribe()
    }

    async init(canvas: HTMLCanvasElement) {
        if (this.$renderer()) throw new Error("Renderer already initialized")
        this.initPromise = this.initImpl(canvas).finally(() => {
            this.initPromise = undefined
        })
        await this.initPromise
    }

    private async initImpl(canvas: HTMLCanvasElement) {
        const antialias = false
        const alpha = true

        const renderer = await (async (): Promise<THREE.WebGPURenderer> => {
            if (!this.$forceWebGl()) {
                const powerPreference = "high-performance"
                const adapter = await navigator.gpu?.requestAdapter({powerPreference})
                const adapterLimits = adapter?.limits
                const requiredLimits: Record<string, number> = {}
                if (adapterLimits?.maxTextureDimension2D) requiredLimits.maxTextureDimension2D = adapterLimits.maxTextureDimension2D
                if (adapterLimits?.maxBufferSize) requiredLimits.maxBufferSize = adapterLimits.maxBufferSize

                return new THREE.WebGPURenderer({
                    canvas,
                    powerPreference,
                    antialias,
                    alpha,
                    requiredLimits: Object.keys(requiredLimits).length > 0 ? requiredLimits : undefined,
                })
            } else {
                return new THREE.WebGPURenderer({
                    canvas,
                    antialias,
                    alpha,
                    forceWebGL: true,
                })
            }
        })()

        renderer.autoClear = false
        renderer.setClearColor(0x000000, 0)

        await renderer.init()

        this.$renderer.set(renderer)
        this.logger.log("Renderer initialized in " + (this.isWebGPUMode() ? "WebGPU" : "WebGL") + " mode")

        this.materialManagerService.$threeRenderer.set(renderer)
        this.redraw()
    }

    async getRenderer(): Promise<THREE.WebGPURenderer> {
        const rendererPre = this.$renderer()
        if (rendererPre) return rendererPre

        if (this.initPromise) {
            await this.initPromise
        }
        const rendererPost = this.$renderer()
        if (!rendererPost) throw new Error("Renderer is not initialized")
        return rendererPost
    }

    resizeRenderer(width: number, height: number, pixelRatio: number) {
        const renderer = this.$renderer()
        if (!renderer) return
        renderer.setPixelRatio(pixelRatio)
        renderer.setSize(width, height)
        this.requestedResize.next()
    }

    private updateScene() {
        const scene = this.$managedScene()

        const toDelete = new Set<ObjectId>()
        for (const [id] of this.objectCache) toDelete.add(id)

        const getThreeObject = (sceneNode: SceneNodes.SceneNode) => {
            const cachedThreeObject = this.objectCache.get(sceneNode.id)
            if (cachedThreeObject) return cachedThreeObject

            const Factory = this.threeFactory[sceneNode.type]
            if (!Factory) {
                return undefined
            }

            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const threeSceneManager = this
            const threeObject = new Factory(this, function (this: ThreeObject) {
                threeSceneManager.threeObjectUpdated.next({threeObject: this, asyncUpdate: true})
                const scene = threeSceneManager.$managedScene()
                const managedSceneNode = scene.find((x) => x.sceneNode.id === this.getSceneNode().id)
                if (managedSceneNode?.visible) threeSceneManager.redraw()
            })

            this.objectCache.set(sceneNode.id, threeObject)
            return threeObject
        }

        let needsUpdate = false
        let needsShadowMapUpdate = false
        let geometryChanged = false
        if (this.$renderer()) {
            for (const {sceneNode, visible} of scene) {
                const threeObject = getThreeObject(sceneNode)
                if (threeObject) {
                    if (threeObject.update(sceneNode)) {
                        this.threeObjectUpdated.next({threeObject: threeObject, asyncUpdate: false})

                        if (visible) {
                            if (
                                threeObject instanceof ThreeMesh ||
                                threeObject instanceof ThreeAreaLight ||
                                threeObject instanceof ThreePointLight ||
                                threeObject instanceof ThreeSunLight ||
                                threeObject instanceof ThreeSeam
                            )
                                needsShadowMapUpdate = true
                            if (threeObject instanceof ThreeMesh && threeObject.geometryOrTransformChangedInLastSetup) geometryChanged = true
                            needsUpdate = true
                        }
                    }

                    const threeRenderObject = threeObject.getRenderObject()
                    if (visible) {
                        if (!threeRenderObject.parent) this.baseScene.add(threeRenderObject)
                    } else {
                        if (threeRenderObject.parent) this.baseScene.remove(threeRenderObject)
                    }
                    toDelete.delete(sceneNode.id)
                }
            }
        }

        for (const id of toDelete) {
            const threeObject = this.objectCache.get(id)
            if (threeObject) {
                const threeRenderObject = threeObject.getRenderObject()
                if (threeRenderObject.parent) {
                    this.baseScene.remove(threeRenderObject)
                    if (
                        threeObject instanceof ThreeMesh ||
                        threeObject instanceof ThreeAreaLight ||
                        threeObject instanceof ThreePointLight ||
                        threeObject instanceof ThreeSunLight ||
                        threeObject instanceof ThreeSeam
                    )
                        needsShadowMapUpdate = true
                    if (threeObject instanceof ThreeMesh) geometryChanged = true
                    needsUpdate = true
                }
                threeObject.dispose(true)
                this.objectCache.delete(id)
            }
        }

        if (needsUpdate) this.redraw()
        if (needsShadowMapUpdate) untracked(() => this.progressiveLightMapUpdate.next())
        if (geometryChanged) this.sceneGeometryChanged.next()
    }

    modifyBaseScene(modifier: (scene: THREE.Scene) => void) {
        modifier(this.baseScene)
    }

    getSceneReference() {
        return this.baseScene
    }

    getManagedThreeReferences() {
        return this.$managedScene()
            .map((x) => {
                const threeObject = this.objectCache.get(x.sceneNode.id)
                if (!threeObject) return undefined
                return threeObject.getRenderObject()
            })
            .filter((x): x is THREE.Object3D => x !== undefined)
    }

    ngOnDestroy() {
        if (this.progressiveLightMap) {
            this.progressiveLightMap.dispose()
            this.progressiveLightMap = undefined
        }

        for (const threeObject of this.objectCache.values()) threeObject.dispose(true)
        this.objectCache.clear()

        this.$renderer()?.dispose()
        this.$renderer.set(undefined)
        this.materialManagerService.$threeRenderer.set(undefined)
    }

    async sync(waitForOptionalTasks: boolean): Promise<void> {
        await this.sceneManagerService.sync(waitForOptionalTasks)
        this.updateScene()
        while (this.sceneManagerService.requiresSync(waitForOptionalTasks)) {
            await this.sceneManagerService.sync(waitForOptionalTasks)
            this.updateScene()
        }
    }

    private redrawImpl(taaFrame: number | null = null, crossfadeAlpha = 1.0) {
        this.updatePendingFrameId = null

        const renderer = this.$renderer()
        if (!renderer) return

        this.logger.log("render")
        renderer.clear()
        this.requestedRedraw.next({taaFrame, crossfadeAlpha})
        if (taaFrame === null) this.nonTaaFrameFinished.next()
    }

    redraw(options?: {immediate?: boolean; strategy?: "from-scratch" | "integrate"}) {
        this.taaGeneration++

        if (options?.strategy === "integrate") {
            this.integrateTaaSubject.next()
            return
        }

        if (options?.immediate) {
            this.redrawImpl()
            return
        }

        if (this.updatePendingFrameId !== null) return
        else
            this.updatePendingFrameId = requestAnimationFrame(() => {
                this.redrawImpl()
            })
    }

    addShadowMapMesh(mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>) {
        if (!this.shadowMapMeshes.has(mesh)) {
            this.shadowMapMeshes.add(mesh)
            this.requestShadowMapUpdate()
            return true
        }

        return false
    }

    removeShadowMapMesh(mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>, willBeDisposed: boolean) {
        if (this.shadowMapMeshes.has(mesh)) {
            this.shadowMapMeshes.delete(mesh)
            if (willBeDisposed && this.progressiveLightMap) this.progressiveLightMap.onDisposingMesh(mesh)
            this.requestShadowMapUpdate()
            return true
        }

        return false
    }

    detachShadowMapMesh(mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>) {
        const {material} = mesh
        if (!(material instanceof ThreeColormassMaterial)) return

        const newMaterial = this.materialManagerService.acquireVariation(material, (material): material is ThreeColormassMaterial => {
            return material instanceof ThreeColormassMaterial && material.uvShadowMapNode === null
        })

        if (newMaterial.uvShadowMapNode !== null) {
            newMaterial.uvShadowMapNode = null
            newMaterial.needsUpdate = true
        }

        this.materialManagerService.releaseMaterial(material)
        mesh.material = newMaterial
    }

    requestShadowMapUpdate() {
        this.shadowMapMeshesChanged.next()
    }

    getThreeObject(id: ObjectId) {
        return this.objectCache.get(id)
    }

    exchangedShadowMapMeshTexture(mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>) {
        if (this.progressiveLightMap && this.progressiveLightMap.has(mesh)) {
            const {material} = mesh
            if (!(material instanceof ThreeColormassMaterial)) return

            const newMaterial = this.materialManagerService.acquireVariation(material, (material): material is ThreeColormassMaterial => {
                return material instanceof ThreeColormassMaterial && material.uvShadowMapNode !== null
            })

            const newShadowMap = this.progressiveLightMap.getUVShadowMap()
            if (newMaterial.uvShadowMapNode?.value !== newShadowMap) {
                newMaterial.uvShadowMapNode = TSL.texture(newShadowMap)
                newMaterial.needsUpdate = true
            }

            this.materialManagerService.releaseMaterial(material)
            mesh.material = newMaterial
        }
    }

    protected shadowMapMeshesSettled() {
        if (this.progressiveLightMap) {
            this.progressiveLightMap.attachToMeshes(this.shadowMapMeshes)
            this.progressiveLightMapUpdate.next()
        }
    }

    isWebGPUMode() {
        const renderer = this.$renderer()
        if (!renderer) return false
        return (renderer.backend as {isWebGLBackend?: true; isWebGPUBackend?: true}).isWebGPUBackend === true
    }

    onPreRender(camera: THREE.Camera, taaFrame: number | null) {
        this.preRender.next({camera, taaFrame})
    }
}
